import React, { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Plus, X, Save, Edit2, Trash2, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
function SortableItem({ id, opt, index, isEditing, onUpdate, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isEditing) {
    return (
      <div 
        ref={setNodeRef} 
        style={style} 
        className={`flex items-center space-x-2 bg-white p-2 rounded-lg border ${isDragging ? 'border-[#4A6741] shadow-lg' : 'border-gray-200'}`}
      >
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5" />
        </button>
        <Input
          placeholder="ID"
          value={opt.id}
          onChange={(e) => onUpdate(index, 'id', e.target.value)}
          className="flex-1"
        />
        <Input
          placeholder="Display Name"
          value={opt.name}
          onChange={(e) => onUpdate(index, 'name', e.target.value)}
          className="flex-1"
        />
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-red-500 hover:text-red-700 p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // View mode
  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border ${isDragging ? 'border-[#4A6741] shadow-lg' : 'border-gray-200'}`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1">
        <p className="font-medium text-gray-900 text-sm">{opt.name}</p>
        <p className="text-xs text-gray-500">ID: {opt.id}</p>
      </div>
    </div>
  );
}

export default function OptionListsManager() {
  const [optionLists, setOptionLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingList, setEditingList] = useState(null);
  const [expandedList, setExpandedList] = useState(null);
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [newList, setNewList] = useState({ name: '', category: '', options: [] });

  useEffect(() => {
    loadOptionLists();
  }, []);

  const loadOptionLists = async () => {
    try {
      const response = await api.get('/admin/option-lists');
      setOptionLists(response.data);
    } catch (error) {
      toast.error('Failed to load option lists');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateList = async () => {
    if (!newList.name || !newList.category) {
      toast.error('Name and category are required');
      return;
    }
    
    try {
      await api.post('/admin/option-lists', newList);
      toast.success('Option list created');
      setShowNewListForm(false);
      setNewList({ name: '', category: '', options: [] });
      loadOptionLists();
    } catch (error) {
      toast.error('Failed to create option list');
    }
  };

  const handleUpdateList = async (listId, updatedData) => {
    try {
      await api.put(`/admin/option-lists/${listId}`, updatedData);
      toast.success('Option list updated');
      setEditingList(null);
      loadOptionLists();
    } catch (error) {
      toast.error('Failed to update option list');
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this option list?')) return;
    
    try {
      await api.delete(`/admin/option-lists/${listId}`);
      toast.success('Option list deleted');
      loadOptionLists();
    } catch (error) {
      toast.error('Failed to delete option list');
    }
  };

  const addOptionToNewList = () => {
    setNewList(prev => ({
      ...prev,
      options: [...prev.options, { id: '', name: '' }]
    }));
  };

  const updateNewListOption = (index, field, value) => {
    setNewList(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const removeNewListOption = (index) => {
    setNewList(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const handleNewListDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setNewList(prev => {
        const oldIndex = prev.options.findIndex((_, i) => `new-${i}` === active.id);
        const newIndex = prev.options.findIndex((_, i) => `new-${i}` === over.id);
        return {
          ...prev,
          options: arrayMove(prev.options, oldIndex, newIndex)
        };
      });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (loading) {
    return (
      <div className="text-center py-12" data-testid="option-lists-loading">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading option lists...</p>
      </div>
    );
  }

  return (
    <div data-testid="option-lists-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Product Option Lists
          </h2>
          <p className="text-gray-600 mt-1">Manage your centralized product configuration options. Drag items to reorder.</p>
        </div>
        <Button 
          onClick={() => setShowNewListForm(true)}
          className="bg-[#334155] text-white hover:bg-[#475569]"
          data-testid="create-option-list-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Option List
        </Button>
      </div>

      {/* New List Form */}
      {showNewListForm && (
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6" data-testid="new-option-list-form">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Create New Option List</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>List Name *</Label>
              <Input
                value={newList.name}
                onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                placeholder="e.g., Shaft Flex Options"
                data-testid="new-list-name-input"
              />
            </div>
            <div>
              <Label>Category Key *</Label>
              <Input
                value={newList.category}
                onChange={(e) => setNewList({ ...newList, category: e.target.value })}
                placeholder="e.g., shaft_flex"
                data-testid="new-list-category-input"
              />
              <p className="text-xs text-gray-500 mt-1">Used internally to match options to products</p>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label>Options (drag to reorder)</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={addOptionToNewList}
                data-testid="add-new-list-option-btn"
              >
                <Plus className="h-3 w-3 mr-1" /> Add Option
              </Button>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleNewListDragEnd}
            >
              <SortableContext
                items={newList.options.map((_, i) => `new-${i}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {newList.options.map((opt, index) => (
                    <SortableItem
                      key={`new-${index}`}
                      id={`new-${index}`}
                      opt={opt}
                      index={index}
                      isEditing={true}
                      onUpdate={updateNewListOption}
                      onRemove={removeNewListOption}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="flex items-center justify-end space-x-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowNewListForm(false);
                setNewList({ name: '', category: '', options: [] });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateList}
              className="bg-[#334155] text-white hover:bg-[#475569]"
              data-testid="save-new-option-list-btn"
            >
              <Save className="h-4 w-4 mr-2" /> Create List
            </Button>
          </div>
        </div>
      )}

      {/* Existing Option Lists */}
      <div className="space-y-4">
        {optionLists.map((list) => (
          <OptionListCard
            key={list.list_id}
            list={list}
            isExpanded={expandedList === list.list_id}
            isEditing={editingList === list.list_id}
            onToggleExpand={() => setExpandedList(expandedList === list.list_id ? null : list.list_id)}
            onEdit={() => setEditingList(list.list_id)}
            onCancelEdit={() => setEditingList(null)}
            onSave={(updatedData) => handleUpdateList(list.list_id, updatedData)}
            onDelete={() => handleDeleteList(list.list_id)}
            onReorder={(newOptions) => {
              // Update local state immediately without reload
              setOptionLists(prev => prev.map(l => 
                l.list_id === list.list_id ? { ...l, options: newOptions } : l
              ));
            }}
          />
        ))}
      </div>

      {optionLists.length === 0 && !showNewListForm && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600 mb-4">No option lists found</p>
          <Button 
            onClick={() => setShowNewListForm(true)}
            className="bg-[#334155] text-white hover:bg-[#475569]"
          >
            Create Your First Option List
          </Button>
        </div>
      )}
    </div>
  );
}

function OptionListCard({ list, isExpanded, isEditing, onToggleExpand, onEdit, onCancelEdit, onSave, onDelete, onReorder }) {
  const [editData, setEditData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (isEditing) {
      setEditData({
        name: list.name,
        category: list.category,
        options: [...list.options]
      });
    }
  }, [isEditing, list]);

  const addOption = () => {
    setEditData(prev => ({
      ...prev,
      options: [...prev.options, { id: '', name: '' }]
    }));
  };

  const updateOption = (index, field, value) => {
    setEditData(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const removeOption = (index) => {
    setEditData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setEditData(prev => {
        const oldIndex = prev.options.findIndex((_, i) => `edit-${list.list_id}-${i}` === active.id);
        const newIndex = prev.options.findIndex((_, i) => `edit-${list.list_id}-${i}` === over.id);
        return {
          ...prev,
          options: arrayMove(prev.options, oldIndex, newIndex)
        };
      });
    }
  };

  const handleViewDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = list.options.findIndex((_, i) => `view-${list.list_id}-${i}` === active.id);
    const newIndex = list.options.findIndex((_, i) => `view-${list.list_id}-${i}` === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newOptions = arrayMove([...list.options], oldIndex, newIndex);
    
    // Update local state immediately (no reload)
    onReorder(newOptions);
    
    // Save to server in background
    setIsSaving(true);
    try {
      await api.put(`/admin/option-lists/${list.list_id}`, {
        ...list,
        options: newOptions
      });
      toast.success('Order saved');
    } catch (error) {
      toast.error('Failed to save order');
      // Revert on error
      onReorder(list.options);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
      data-testid={`option-list-card-${list.category}`}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
        onClick={onToggleExpand}
      >
        <div className="flex items-center space-x-4">
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
          <div>
            <h3 className="font-semibold text-gray-900">{list.name}</h3>
            <p className="text-sm text-gray-500">
              Category: <code className="bg-gray-200 px-1 rounded">{list.category}</code>
              <span className="mx-2">•</span>
              {list.options.length} options
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onEdit}
            className="p-2 text-gray-600 hover:text-[#334155] hover:bg-gray-200 rounded-lg transition-colors"
            data-testid={`edit-${list.category}-btn`}
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            data-testid={`delete-${list.category}-btn`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-200">
          {isEditing && editData ? (
            /* Edit Mode */
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>List Name</Label>
                  <Input
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Category Key</Label>
                  <Input
                    value={editData.category}
                    onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                  />
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <Label>Options ({editData.options.length}) - Drag to reorder</Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={addOption}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={editData.options.map((_, i) => `edit-${list.list_id}-${i}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {editData.options.map((opt, index) => (
                        <SortableItem
                          key={`edit-${list.list_id}-${index}`}
                          id={`edit-${list.list_id}-${index}`}
                          opt={opt}
                          index={index}
                          isEditing={true}
                          onUpdate={updateOption}
                          onRemove={removeOption}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>

              <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-200">
                <Button variant="outline" onClick={onCancelEdit}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => onSave(editData)}
                  className="bg-[#334155] text-white hover:bg-[#475569]"
                >
                  <Save className="h-4 w-4 mr-2" /> Save Changes
                </Button>
              </div>
            </div>
          ) : (
            /* View Mode with Drag & Drop */
            <div>
              <p className="text-sm text-gray-500 mb-3 flex items-center">
                <GripVertical className="h-4 w-4 mr-1" />
                Drag items to reorder
                {isSaving && (
                  <span className="ml-2 flex items-center text-[#4A6741]">
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-solid border-[#4A6741] border-r-transparent mr-1"></div>
                    Saving...
                  </span>
                )}
              </p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleViewDragEnd}
              >
                <SortableContext
                  items={list.options.map((_, i) => `view-${list.list_id}-${i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {list.options.map((opt, index) => (
                      <SortableItem
                        key={`view-${list.list_id}-${index}`}
                        id={`view-${list.list_id}-${index}`}
                        opt={opt}
                        index={index}
                        isEditing={false}
                        onUpdate={() => {}}
                        onRemove={() => {}}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              {list.options.length === 0 && (
                <p className="text-gray-500 text-center py-4">No options defined. Click edit to add options.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
